import { shapeBattle, shapeHero, shapeRules, shapeStats, shapeStructure } from './shapes.ts';
import {
  BOSSES,
  AFFINITY,
  CRIT,
  SEASON_BY_ID,
  type SeasonId,
  FORTIFY_HP_BP,
  HEROES,
  LEVEL_STATS,
  LEVEL_STATS_FULL_AT,
  SKILL_RANK_LEVELS,
  type HeroStatKey,
  LEVEL_GROWTH_BP,
  LEVEL_GROWTH_KNEE,
  LEVEL_GROWTH_LATE_BP,
  SKILL_LEVEL,
  LEVEL_HP_BP,
  MATERIAL_HP_BP,
  NIGHTMARES,
  STRUCTURES,
  type Enchant,
  type ForkId,
  type SkillDef,
  type SkillId,
  type GlyphId,
  SKILLS,
  type HeroClass,
  type RelicId,
  type StructureType,
  type Material,
  type StructLevel,
  type DamageType,
  type UniqueId,
  type PaladinAura,
  type BranchId,
} from '@somnhold/content';
import { cellCenter, cellId } from '../grid.ts';
import { hexLen, hexToWorld } from '../hex.ts';
import { idiv, imax, imin, mulBp, mulBpRound } from '../math/fixed.ts';
import { markFrost } from './frost.ts';
import { deriveSeed, rngChance, seedRng } from '../math/rng.ts';
import { placeCracks } from './ascuas.ts';
import { startEscort } from './escort.ts';
import { newPlunder } from './plunder.ts';
import { newDungeonState } from './dungeon.ts';
import { newCellars } from './expedition.ts';
import { newMaze, type MazeParams } from './maze.ts';
import { createMaze } from '../labyrinth.ts';
import { placeDeep } from './deep.ts';
import { rankedSkill } from './skills.ts';
import { newPlanState } from './plan.ts';
import { startSima, type SimaParams } from './sima.ts';
import type {
  BattleMode,
  BattleRules,
  BattleState,
  DungeonSpawn,
  ExpArea,
  GhostProfile,
  Hero,
  HeroStats,
  MapDef,
  PlanParams,
  PlayerId,
  PlayerStats,
  Spawn,
  Structure,
  TickInputs,
} from '../types.ts';

export interface StructureInit {
  type: StructureType;
  level: StructLevel;
  material: Material;
  q: number;
  r: number;
  /** Current HP (defaults to max). */
  hp?: number;
  enchant?: Enchant | null;
  fortified?: boolean;
  charges?: number;
  cellKey?: string;
  /** Terraced land: the other cells a three-cell building takes (it blocks them all and is hit through any). */
  foot?: { q: number; r: number }[];
  /** Terraced land: a tower's branch, and a tower mounted on a wall run (docs/OBRAS.md). */
  branch?: BranchId;
  mount?: { type: StructureType; level: StructLevel };
}

/** Additive gear modifiers in bp. */
export interface GearMods {
  damageBp: number;
  hpBp: number;
  attackSpeedBp: number;
  cooldownBp: number;
  armorBp: number;
  /** Certeza: critical chance from gear (already at its worth, see attrValue). */
  critBp?: number;
  /** Dread (a condition that lasts days): the hero starts every fight frightened this long. */
  dreadTicks?: number;
  /** Affinity: resistance to each damage type (already at its worth, the opposite's share taken off). */
  resBp?: Partial<Record<DamageType, number>>;
  /** Rare pieces' effects (GEAR_EFFECT_FX), only when worn. */
  burnBp?: number;
  lifestealBp?: number;
  thornsBp?: number;
  swiftBp?: number;
  furyGearBp?: number;
  executeBp?: number;
  chainBp?: number;
  frostBp?: number;
  cleaveBp?: number;
  guardBp?: number;
  echoTicks?: number;
  bannerBp?: number;
  /** Base traits (docs/EQUIPO.md §2): the basic attack's blow and reach, melee at this reach, harder on elites and bosses. */
  attackBp?: number;
  rangeBp?: number;
  melee?: number;
  bigFoeBp?: number;
  /** A two-handed sword's cleave (the stronger of this and a cleave effect counts). */
  cleaveTraitBp?: number;
  /** Every `stunEvery` blows that land stun for `stunTicks`; the blow carries on into the next nightmare. */
  stunEvery?: number;
  stunTicks?: number;
  pierce?: boolean;
  /** A shield's block, an orb's skills, a holy symbol's heals, a toolkit's turrets and automaton. */
  blockBp?: number;
  skillBp?: number;
  healGearBp?: number;
  summonBp?: number;
  /** Unique pieces worn (their effects hook into the class's skills). */
  uniques?: UniqueId[];
  /** Each unique's awakening stage, alongside `uniques` (absent: none awake). */
  uniqueLv?: number[];
}

export interface HeroInit {
  pid: PlayerId;
  name: string;
  cls: HeroClass;
  level: number;
  control: 'live' | 'ghost' | 'merc';
  /** Damage multiplier: 10000 live, 7000 × activity for ghosts… */
  powerBp: number;
  post: number;
  /** Sent "wherever needed": as a ghost it goes where the bastion hurts most instead of keeping to its post. */
  roam?: boolean;
  profile?: GhostProfile;
  relics?: RelicId[];
  /** Relics awakened at the forge (only the carried ones count). */
  awakened?: RelicId[];
  gear?: Partial<GearMods>;
  /** What shows of its gear on the miniature (heroLook): cosmetic, the sim never reads it. */
  look?: string;
  /** The variant carried in each slot (docs/PODERES.md §1; none when absent). */
  variants?: Variants;
  /** The skill carried in each slot (the class's own when absent). */
  skills?: [SkillId, SkillId, SkillId];
  /** A paladin's aura (devotion when absent). */
  aura?: PaladinAura;
  /** The glyph carved on each button (docs/GLIFOS.md; none when absent). */
  glyphs?: [GlyphId | null, GlyphId | null, GlyphId | null];
  /** The form of each unique piece worn (docs/UNICAS.md §3, content `FORM_BIT`; none when absent), as the campaign carries it. */
  forms?: Partial<Record<UniqueId, number>>;
  /** Fading ghosts hold fewer nightmares: block capacity × this weight (bp, default full). */
  presenceBp?: number;
}

export interface BattleParams {
  mode: BattleMode;
  seed: number;
  map: MapDef;
  structures: StructureInit[];
  heroes: HeroInit[];
  keepHp: number;
  keepMaxHp: number;
  marea: number;
  wave: number;
  spawns: Spawn[];
  bossHp?: number;
  purse?: { wood: number; stone: number; crystal: number; dust: number } | null;
  /** Incursion: ticks to fill the extraction bar. */
  objectiveTicks?: number;
  /** Incursion: how much faster the extraction (and the clock to the next wave) runs while the field is empty. */
  objectiveQuietRate?: number;
  rules?: BattleRules;
  /** A holiday season (see SEASONS): tougher nightmares and its own creatures. */
  season?: SeasonId;
  /** The clan hall's war drums (a barbarian fights the wave): everyone runs faster at the start. */
  drums?: boolean | number;
  /** The wave's battle plan (docs/TACTICAS.md). */
  plan?: PlanParams;
  /** Elite incursion: what waits in each room and the Warden's health. */
  /** `hallWins`: no Warden; reaching the last hall wins (saga descents). */
  dungeon?: { spawns: DungeonSpawn[]; bossHp: number; dmgBp: number; hallWins?: boolean; /** Deep crypt: each floor's guardian's health. */ guardianHp?: number[] };
  /** The Chasm (docs/SIMA.md): where the run starts, the week, the era's nightmares and the party's scaling. */
  sima?: SimaParams;
  /** Plunder (docs/MISIONES.md): where the old garrison's caches lie. */
  plunder?: { caches: { kind: 'chest' | 'coffer'; q: number; r: number }[] };
  /** A group expedition (docs/EXPEDICIONES.md): which rooms the layout has and how its doors join them. */
  expedition?: { kind: 'cellars'; areas: ExpArea[]; links: [ExpArea, ExpArea][] };
  /** The Furrows (docs/LABERINTOS.md): the week's labyrinth and what the group already has of it. */
  maze?: MazeParams;
}

/**
 * A spectre before it has learned its player: as the learned profiles of real players do (prod, 2026-10-30), the
 * nearest nightmare first (the weakest is mostly dying already), melee close and the rest well inside their reach,
 * and back at 40 % of life. It stays near its post (the warrior at its gate): out in the open the nightmares stream
 * past it, and on an incursion the node falls.
 */
export const DEFAULT_PROFILE = (cls: HeroClass): GhostProfile => ({
  preferredRange: HEROES[cls].range <= 1500 ? 700 : imin(HEROES[cls].range - 500, 3500),
  gateLeash: cls === 'warrior' ? 2500 : 3500,
  targetPriority: 'nearest',
  skillThresholds: { challenge: 3, oath: 5, storm: 4, arrowRain: 4, fireball: 2, multishot: 2 },
  retreatHpBp: 4000,
  repairsBetweenWaves: cls === 'artificer',
  samples: 0,
});

/** Stats at a level: +8 % per level up to the knee, +3 % past it, multiplicative, rounded (SPEC §6). */
export const levelScale = (v: number, level: number): number => {
  let out = v;
  for (let i = 1; i < level; i++) out = mulBpRound(out, i < LEVEL_GROWTH_KNEE ? LEVEL_GROWTH_BP : LEVEL_GROWTH_LATE_BP);
  return out;
};

/**
 * What the level adds to a skill's amounts (bp): SKILL_LEVEL.shareBp of the level growth (`levelScale`).
 * Memoized by level and share (the share is content, so a balance run that changes it gets its own entries).
 */
const skillLevelMemo = new Map<number, number>();
export const skillLevelBp = (level: number): number => {
  const key = level * 100000 + SKILL_LEVEL.shareBp;
  let v = skillLevelMemo.get(key);
  if (v === undefined) {
    v = 10000 + idiv((levelScale(10000, level) - 10000) * SKILL_LEVEL.shareBp, 10000);
    skillLevelMemo.set(key, v);
  }
  return v;
};

/** The variant carried in each slot (null: the skill alone). */
export type Variants = [ForkId | null, ForkId | null, ForkId | null];

/**
 * What the level adds to a stat (bp): a share of the class's `LEVEL_STATS` that grows with the square
 * of the levels past the first, so little comes early (the ranks came first on the old tree) and all
 * of it at `LEVEL_STATS_FULL_AT`.
 */
export const levelStat = (cls: HeroClass, level: number, stat: HeroStatKey): number => {
  const n = imax(0, imin(level, LEVEL_STATS_FULL_AT) - 1);
  const full = LEVEL_STATS_FULL_AT - 1;
  return idiv((LEVEL_STATS[cls][stat] ?? 0) * n * n, full * full);
};

/** The rank skills reach at a level (1 to 3): every slot alike, whatever skill it carries. */
export const rankAt = (level: number): number => 1 + SKILL_RANK_LEVELS.filter((l) => level >= l).length;

/** Skills whose `amount` is life (a heal, a shield, a body's health) and grows with the level as their damage does (the rest are counts or shares). */
const LIFE_AMOUNTS: ReadonlySet<SkillId> = new Set<SkillId>(['mend', 'sanctuary', 'faithRay', 'piercingLight', 'aegis', 'automaton', 'decoy', 'forestAwakening']);

/** A skill as it lands at a level, for what the player reads: its damage, and its amount when that is life, grown with the level (`skillLevelBp`). */
export const levelSkill = (sk: SkillDef, level: number): SkillDef => {
  const bp = skillLevelBp(level);
  return { ...sk, damage: mulBp(sk.damage, bp), amount: LIFE_AMOUNTS.has(sk.id) ? mulBp(sk.amount, bp) : sk.amount };
};

/** A hero's skill in a slot at a level, with the variant and glyph carried (for the hero sheet); the class's own unless `skill` says. */
export const skillAt = (cls: HeroClass, level: number, slot: 0 | 1 | 2, variant: ForkId | null = null, skill: SkillId = HEROES[cls].skills[slot], glyph: GlyphId | null = null): SkillDef =>
  levelSkill(rankedSkill(SKILLS[skill], rankAt(level), variant, glyph), level);

/** A hero's affinities, each held between AFFINITY.minBp and AFFINITY.maxBp (types left at 0 dropped). */
const clampRes = (r: Partial<Record<DamageType, number>>): Partial<Record<DamageType, number>> => {
  const out: Partial<Record<DamageType, number>> = {};
  for (const k of Object.keys(r).sort() as DamageType[]) {
    const v = imax(AFFINITY.minBp, imin(AFFINITY.maxBp, r[k] ?? 0));
    if (v !== 0) out[k] = v;
  }
  return out;
};

export const heroStats = (init: HeroInit): HeroStats => {
  const d = HEROES[init.cls];
  const t = (k: HeroStatKey): number => levelStat(init.cls, init.level, k);
  const gg = init.gear ?? {};
  const g = {
    damageBp: (gg.damageBp ?? 0) + t('damage'),
    hpBp: (gg.hpBp ?? 0) + t('hp'),
    attackSpeedBp: (gg.attackSpeedBp ?? 0) + t('attackSpeed'),
    cooldownBp: (gg.cooldownBp ?? 0) + t('cooldown'),
    armorBp: (gg.armorBp ?? 0) + t('armor'),
  };
  const hp = levelScale(d.hp, init.level);
  const atk = levelScale(d.attack, init.level);
  return shapeStats({
    maxHp: mulBp(hp, 10000 + (g.hpBp ?? 0)),
    attack: gg.attackBp ? mulBp(mulBp(atk, 10000 + (g.damageBp ?? 0)), 10000 + gg.attackBp) : mulBp(atk, 10000 + (g.damageBp ?? 0)),
    reload: idiv(d.reload * 10000, 10000 + (g.attackSpeedBp ?? 0)),
    range: gg.melee ?? mulBp(d.range, 10000 + t('range') + (gg.rangeBp ?? 0)),
    splash: d.splash,
    armorBp: d.armorBp + (g.armorBp ?? 0),
    speed: gg.swiftBp ? mulBp(d.speed, 10000 + gg.swiftBp) : d.speed,
    powerBp: init.powerBp,
    ...(g.damageBp ? { damageBp: g.damageBp } : {}),
    ...(t('healing') + (gg.healGearBp ?? 0) ? { healBp: 10000 + t('healing') + (gg.healGearBp ?? 0) } : {}),
    // A fading ghost also casts less often: cooldowns stretch by 1 / presence.
    cooldownBp: idiv((10000 - (g.cooldownBp ?? 0)) * 10000, init.presenceBp && init.presenceBp > 0 ? init.presenceBp : 10000),
    blockCapacity: Math.trunc((d.blockCapacity * (init.presenceBp ?? 10000)) / 10000),
    critBp: imin(CRIT.maxBp, (CRIT.chanceBp[init.cls] ?? 0) + (gg.critBp ?? 0)),
    critMultBp: CRIT.multBp[init.cls] ?? 15000,
    ...(gg.resBp ? { resBp: clampRes(gg.resBp) } : {}),
    ...(gg.burnBp ? { burnBp: gg.burnBp } : {}),
    ...(gg.lifestealBp ? { lifestealBp: gg.lifestealBp } : {}),
    ...(gg.thornsBp ? { thornsBp: gg.thornsBp } : {}),
    ...(gg.furyGearBp ? { furyGearBp: gg.furyGearBp } : {}),
    ...(gg.executeBp ? { executeBp: gg.executeBp } : {}),
    ...(gg.chainBp ? { chainBp: gg.chainBp } : {}),
    ...(gg.frostBp ? { frostBp: gg.frostBp } : {}),
    ...(gg.cleaveBp || gg.cleaveTraitBp ? { cleaveBp: imax(gg.cleaveBp ?? 0, gg.cleaveTraitBp ?? 0) } : {}),
    ...(gg.guardBp ? { guardBp: gg.guardBp } : {}),
    ...(gg.echoTicks ? { echoTicks: gg.echoTicks } : {}),
    ...(gg.bannerBp ? { bannerBp: gg.bannerBp } : {}),
    ...(gg.melee ? { melee: true } : {}),
    ...(gg.bigFoeBp ? { bigFoeBp: gg.bigFoeBp } : {}),
    ...(gg.stunEvery ? { stunEvery: gg.stunEvery, stunTicks: gg.stunTicks ?? 0 } : {}),
    ...(gg.pierce ? { pierceGear: true } : {}),
    ...(gg.blockBp ? { blockBp: gg.blockBp } : {}),
    ...(gg.skillBp ? { skillBp: gg.skillBp } : {}),
    ...(gg.summonBp ? { summonBp: gg.summonBp } : {}),
  });
};

export const emptyPlayerStats = (): PlayerStats => ({
  damage: 0,
  kills: 0,
  skills: 0,
  rangeHist: new Array<number>(20).fill(0),
  leashHist: new Array<number>(20).fill(0),
  retreatHist: new Array<number>(10).fill(0),
  choice: [0, 0, 0, 0],
  areaCounts: {},
  structuresRepaired: 0,
  downs: 0,
});

export const structureMaxHp = (type: StructureType, level: StructLevel, material: Material, fortified: boolean): number => {
  const base = STRUCTURES[type].hp;
  let hp = mulBp(mulBp(base, LEVEL_HP_BP[level - 1] as number), MATERIAL_HP_BP[material]);
  if (fortified) hp = mulBp(hp, 10000 + FORTIFY_HP_BP);
  return hp;
};

export const makeStructure = (s: BattleState, init: StructureInit): Structure => {
  const def = STRUCTURES[init.type];
  const maxHp = structureMaxHp(init.type, init.level, init.material, init.fortified ?? false);
  return shapeStructure({
    id: s.nextId++,
    type: init.type,
    level: init.level,
    material: init.material,
    q: init.q,
    r: init.r,
    hp: init.hp === undefined ? maxHp : init.hp > maxHp ? maxHp : init.hp,
    maxHp,
    cd: 0,
    charges: init.charges ?? def.charges ?? 0,
    enchant: init.enchant ?? null,
    fortified: init.fortified ?? false,
    cursed: false,
    purify: 0,
    aim: 0,
    buffTicks: 0,
    buffBp: 0,
    destroyed: def.hp > 0 && init.hp !== undefined && init.hp <= 0,
    cellKey: init.cellKey ?? `${init.q},${init.r}`,
    ...(init.foot ? { foot: init.foot.map((h) => ({ q: h.q, r: h.r })) } : {}),
    ...(init.branch ? { branch: init.branch } : {}),
    ...(init.mount ? { mount: { type: init.mount.type, level: init.mount.level } } : {}),
  });
};

/** Where heroes stand at the start and respawn: the plaza cell facing their post. */
export const heroHome = (s: BattleState, post: number): { x: number; y: number } => {
  const entry = s.map.dungeon?.rooms[0];
  if (entry) {
    // The crypt: side by side in the entrance hall; a party past four, a second row behind.
    const c = hexToWorld(entry.q, entry.r);
    if (post < 4) return { x: c.x + (post - 1) * 700, y: c.y + (post & 1) * 500 };
    return { x: c.x + (post - 5) * 700, y: c.y - 700 - (post & 1) * 500 };
  }
  // The dragon's clearing: by a harpoon or in the middle (docs/UNICAS.md §4.11).
  const posts = s.rules?.dragon?.posts;
  if (posts?.length) {
    const p = posts[(post < 0 ? 0 : post) % posts.length] as { x: number; y: number };
    return { x: p.x, y: p.y };
  }
  const sec = s.map.sectors[post];
  if (!sec) {
    const c = hexToWorld(0, 1);
    return { x: c.x, y: c.y };
  }
  // Step from the keep two cells toward the gate.
  const g = hexToWorld(sec.gate.q, sec.gate.r);
  const k = imax(1, hexLen(sec.gate.q, sec.gate.r));
  return { x: idiv(g.x * 2, k), y: idiv(g.y * 2, k) };
};

/** Default post spot of a ghost: inside its gate. */
export const postSpot = (s: BattleState, post: number): { x: number; y: number } => {
  if (s.rules?.dragon?.posts.length) return heroHome(s, post);
  const sec = s.map.sectors[post];
  if (!sec) return hexToWorld(0, 1);
  const g = hexToWorld(sec.gate.q, sec.gate.r);
  const k = imax(1, hexLen(sec.gate.q, sec.gate.r));
  return { x: idiv(g.x * (k - 2), k), y: idiv(g.y * (k - 2), k) };
};

/** The awakened relics a hero carries into the battle (absent when none, so older states hash the same). */
const awakenedOf = (init: HeroInit): { awakened?: RelicId[] } => {
  const a = (init.awakened ?? []).filter((r) => (init.relics ?? []).includes(r));
  return a.length ? { awakened: a } : {};
};

/** The form of each unique worn, parallel to its pieces (absent when none has one, so older states hash the same). */
const formsOf = (init: HeroInit): { uqForm?: number[] } => {
  const ids = init.gear?.uniques;
  if (!ids?.length || !init.forms) return {};
  const out = ids.map((id) => init.forms?.[id] ?? 0);
  return out.some((x) => x !== 0) ? { uqForm: out } : {};
};

export const makeHero = (s: BattleState, init: HeroInit): Hero => {
  const stats = heroStats(init);
  const home = heroHome(s, init.post);
  return shapeHero({
    id: s.nextId++,
    pid: init.pid,
    name: init.name,
    cls: init.cls,
    level: init.level,
    x: home.x,
    y: home.y,
    hp: stats.maxHp,
    stats,
    control: init.control,
    post: init.post,
    profile: init.profile ?? DEFAULT_PROFILE(init.cls),
    relics: init.relics ?? [],
    ...awakenedOf(init),
    ...(init.roam ? { roam: true as const } : {}),
    ...(init.look ? { look: init.look } : {}),
    ...(init.gear?.uniques?.length ? { uq: init.gear.uniques.slice() } : {}),
    ...(init.gear?.uniqueLv?.some((x) => x > 0) ? { uqLv: init.gear.uniqueLv.slice() } : {}),
    ...formsOf(init),
    ...(init.aura && init.aura !== 'devotion' ? { aura: init.aura } : {}),
    moveAcc: 0,
    path: [],
    destX: home.x,
    destY: home.y,
    moving: false,
    target: 0,
    forced: false,
    attackCd: 0,
    skillCd: [0, 0, 0],
    ranks: [rankAt(init.level), rankAt(init.level), rankAt(init.level)],
    forks: init.variants ? [...init.variants] : [null, null, null],
    ...(init.skills && init.skills.some((id, i) => id !== HEROES[init.cls].skills[i]) ? { kit: [...init.skills] as [SkillId, SkillId, SkillId] } : {}),
    // Only a hero with a glyph carries the field: everyone else plays (and hashes) as before.
    ...(init.glyphs && init.glyphs.some((g) => g !== null) ? { glyphs: [...init.glyphs] as [GlyphId | null, GlyphId | null, GlyphId | null] } : {}),
    stun: 0,
    ...(init.gear?.dreadTicks ? { fear: init.gear.dreadTicks } : {}),
    invuln: 0,
    taunt: 0,
    tauntRadius: 0,
    downTicks: 0,
    facing: 0,
    anim: 'idle',
    animT: 0,
    idleTicks: 0,
    purifying: 0,
    ai: { mode: 'hold', t: 0 },
    usedOnce: 0,
    hurtTick: -1000,
    ax: home.x,
    ay: home.y,
  });
};

export const createBattle = (p: BattleParams): BattleState => {
  const s: BattleState = shapeBattle({
    v: 1,
    mode: p.mode,
    tick: 0,
    seed: p.seed >>> 0,
    rng: seedRng(p.seed),
    nextId: 1,
    map: p.map,
    gridRev: 0,
    structures: [],
    turrets: [],
    heroes: [],
    enemies: [],
    projectiles: [],
    zones: [],
    keep: { hp: p.keepHp, maxHp: p.keepMaxHp },
    wave: { marea: p.marea, wave: p.wave, spawns: p.spawns.slice(), next: 0, bossHp: p.bossHp ?? 0, lastStand: false },
    objective: p.objectiveTicks ? { hp: p.keepHp, maxHp: p.keepMaxHp, progress: 0, total: p.objectiveTicks, ...(p.objectiveQuietRate ? { quietRate: p.objectiveQuietRate, quiet: false } : {}) } : null,
    ...(p.rules ? { rules: shapeRules({ ...p.rules }) } : {}),
    ...(p.season ? { season: p.season } : {}),
    ...(p.drums ? { drums: p.drums } : {}),
    ...(p.dungeon && p.map.dungeon ? { dungeon: { ...newDungeonState(p.map.dungeon.rooms.length, p.dungeon.spawns.slice(), p.dungeon.bossHp, p.dungeon.dmgBp), ...(p.dungeon.hallWins ? { hallWins: true } : {}) } } : {}),
    purse: p.purse ?? null,
    started: p.mode !== 'practice',
    waveT: 0,
    result: 'running',
    endTick: 0,
    stats: {
      players: {},
      sectors: p.map.sectors.map(() => ({ leaks: 0, lost: 0, kills: 0, gateFell: false })),
      keepDamage: 0,
      kills: 0,
      dust: 0,
      eliteKills: 0,
      bossKilled: false,
      highlights: [],
    },
  });
  if (p.season) seasonalSpawns(s, p.season);
  const R = p.map.radius;
  // Structures sorted by cell id so iteration order never depends on input order.
  const inits = p.structures
    .filter((x) => cellId(R, x.q, x.r) >= 0)
    .slice()
    .sort((a, b) => cellId(R, a.q, a.r) - cellId(R, b.q, b.r));
  for (const init of inits) s.structures.push(makeStructure(s, init));
  // Under frost: which towers are cold, and the oils that light to warm the rest (docs/PRESAGIOS.md §3.3).
  markFrost(s);
  const heroes = p.heroes.slice().sort((a, b) => (a.pid < b.pid ? -1 : a.pid > b.pid ? 1 : 0));
  for (const h of heroes) {
    const hero = makeHero(s, h);
    // Fog shortens the reach of bows and spells; a sword still reaches what it reaches.
    if (p.rules?.heroRangeBp && hero.cls !== 'warrior') hero.stats.range = mulBp(hero.stats.range, p.rules.heroRangeBp);
    // A blood moon: every blow drinks a little.
    if (p.rules?.heroLifestealBp) hero.stats.lifestealBp = (hero.stats.lifestealBp ?? 0) + p.rules.heroLifestealBp;
    s.heroes.push(hero);
    s.stats.players[h.pid] = emptyPlayerStats();
  }
  placeCracks(s);
  if (p.dungeon?.guardianHp) placeDeep(s, p.dungeon.guardianHp);
  if (p.sima && s.dungeon) startSima(s, p.sima);
  if (p.expedition && s.dungeon) s.dungeon.exp = newCellars(s, p.expedition.areas, p.expedition.links, s.dungeon.spawns);
  if (p.maze && s.dungeon) s.dungeon.maze = newMaze(s, createMaze(p.maze.seed, p.maze.radius).layout, p.maze, s.dungeon.spawns);
  startEscort(s);
  if (p.plunder) s.plunder = newPlunder(s, p.plunder.caches);
  if (p.plan && p.mode === 'wave') s.plan = newPlanState(s, p.plan);
  return s;
};

/**
 * The season's creature joins the fight: one more next to some nightmares (never bosses or warlords),
 * a moment after them, in waves and in the crypt's rooms. Rolled from its own stream, so the rest of
 * the battle's randomness stays as it was.
 */
const seasonalSpawns = (s: BattleState, id: SeasonId): void => {
  const extra = SEASON_BY_ID[id].extra;
  if (!extra) return;
  const rng = seedRng(deriveSeed(s.seed, 31337));
  const out: Spawn[] = [];
  for (const sp of s.wave.spawns) {
    out.push(sp);
    // Never beside a boss of its own (a saga's, an Unsleeping, the Prestacaras): the copy would carry its life and kit.
    if (sp.elite || sp.boss || sp.un || sp.copy || sp.kind in BOSSES || sp.kind === 'boneWall' || !rngChance(rng, extra.chanceBp)) continue;
    out.push({ ...sp, tick: sp.tick + 6, kind: extra.kind, elite: false });
  }
  s.wave.spawns = out.sort((a, b) => a.tick - b.tick);
  const d = s.dungeon;
  if (d) {
    const more: DungeonSpawn[] = [];
    for (const sp of d.spawns) {
      if (sp.elite || !rngChance(rng, extra.chanceBp)) continue;
      more.push({ ...sp, kind: extra.kind, elite: false, q: sp.q, r: sp.r });
    }
    d.spawns = d.spawns.concat(more);
  }
};

/** Spawn point of a sector (first path cell). */
export const spawnPoint = (s: BattleState, sector: number): { x: number; y: number } => {
  const sec = s.map.sectors[sector];
  const h = sec?.path[0];
  if (!h) return hexToWorld(0, s.map.radius - 1);
  return cellCenter(s.map.radius, cellId(s.map.radius, h.q, h.r));
};

export const enemyBase = NIGHTMARES;

/**
 * A battle to replay from its params and the input bundles a live room recorded, set up the way the room
 * set it up: every hero a ghost until its player's `control` input, and a fight that waited in its lobby
 * (the group gathered before the wave's hour) still waiting until its recorded `start`.
 */
export const replayStart = (params: BattleParams, bundles: { tick: number; inputs: TickInputs }[] = []): BattleState => {
  const b = createBattle(params);
  if (!bundles.length) return b;
  for (const h of b.heroes) h.control = 'ghost';
  if (bundles.some((x) => Object.values(x.inputs).some((cmds) => cmds.some((c) => c.t === 'start')))) b.started = false;
  return b;
};
